/**
 * MongoDB 用户管理列表页面
 * 功能：用户列表、搜索筛选、编辑用户、管理会员/积分
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Avatar,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Stars as StarsIcon,
  MonetizationOn as CoinIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  EmojiEvents as EmojiEventsIcon,
} from '@mui/icons-material';

import { userApi } from '../services/mongoUserApi';
import UserEditDialog from '../components/admin/UserEditDialog';
import TierAdjustDialog from '../components/admin/TierAdjustDialog';

// 会员等级配置
const MEMBERSHIP_LEVELS = {
  free: { label: '免费', color: 'default' },
  bronze: { label: '青铜', color: 'info' },
  silver: { label: '白银', color: 'primary' },
  gold: { label: '黄金', color: 'warning' },
  platinum: { label: '铂金', color: 'secondary' },
  diamond: { label: '钻石', color: 'error' },
};

const MongoUserManagement = () => {
  // 列表数据
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 分页
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // 筛选
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');

  // MongoDB连接状态
  const [mongoAvailable, setMongoAvailable] = useState(true);

  // 编辑对话框
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // 等级调整对话框 (v3 LevelUp 2.0)
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [tierUser, setTierUser] = useState(null);

  // 加载用户列表
  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page + 1,
        page_size: pageSize,
      };
      if (statusFilter) params.status = statusFilter;
      if (levelFilter) params.level = levelFilter;
      if (keyword.trim()) params.keyword = keyword.trim();

      const response = await userApi.getList(params);
      setUsers(response.data.users);
      setTotal(response.data.total);
      setMongoAvailable(true);
    } catch (err) {
      setError('加载用户列表失败: ' + (err.message || '未知错误'));
      if (err.message?.includes('MongoDB') || err.response?.status === 500) {
        setMongoAvailable(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    loadUsers();
  }, [page, pageSize]);

  // 搜索
  const handleSearch = () => {
    setPage(0);
    loadUsers();
  };

  // 重置筛选
  const handleReset = () => {
    setKeyword('');
    setStatusFilter('');
    setLevelFilter('');
    setPage(0);
    setTimeout(loadUsers, 50);
  };

  // 编辑用户
  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditDialogOpen(true);
  };

  // 关闭编辑对话框
  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
  };

  // 编辑成功后刷新列表
  const handleEditSuccess = () => {
    loadUsers();
  };

  // 获取状态芯片
  const getStatusChip = (user) => {
    if (user.status.is_banned) {
      return <Chip label="已封禁" color="error" size="small" icon={<BlockIcon />} />;
    }
    if (!user.status.is_active) {
      return <Chip label="已禁用" color="default" size="small" />;
    }
    return <Chip label="正常" color="success" size="small" icon={<CheckCircleIcon />} />;
  };

  // 获取会员等级芯片
  const getMembershipChip = (membership) => {
    const levelConfig = MEMBERSHIP_LEVELS[membership.level];
    return (
      <Chip
        label={levelConfig?.label || membership.level_name}
        color={levelConfig?.color || 'default'}
        size="small"
        icon={<StarsIcon />}
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* 页面标题 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            基于MongoDB的用户数据管理系统 | 共 {total} 位用户
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!mongoAvailable && (
            <Alert severity="warning" sx={{ mr: 2 }}>
              MongoDB连接异常，部分功能可能不可用
            </Alert>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadUsers}
            disabled={loading}
          >
            刷新
          </Button>
        </Box>
      </Box>

      {/* 筛选区域 */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="搜索用户"
              placeholder="用户名/邮箱/昵称"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>状态</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="状态"
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="active">正常</MenuItem>
                <MenuItem value="banned">已封禁</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>会员等级</InputLabel>
              <Select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                label="会员等级"
              >
                <MenuItem value="">全部</MenuItem>
                {Object.entries(MEMBERSHIP_LEVELS).map(([key, config]) => (
                  <MenuItem key={key} value={key}>{config.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleSearch} sx={{ flex: 1 }}>
                搜索
              </Button>
              <Button variant="outlined" onClick={handleReset} sx={{ flex: 1 }}>
                重置
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 错误提示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 用户列表表格 */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>用户</TableCell>
                <TableCell>邮箱</TableCell>
                <TableCell>状态</TableCell>
                <TableCell>会员等级</TableCell>
                <TableCell>积分</TableCell>
                <TableCell>成长等级</TableCell>
                <TableCell>注册时间</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>加载中...</Typography>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">
                      暂无用户数据，请先通过API创建测试用户
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar>
                          {user.profile.avatar ? (
                            <img src={user.profile.avatar} alt="" />
                          ) : (
                            <PersonIcon />
                          )}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {user.profile.nickname || user.username}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            @{user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{getStatusChip(user)}</TableCell>
                    <TableCell>{getMembershipChip(user.membership)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CoinIcon color="warning" fontSize="small" />
                        <Typography variant="body1" fontWeight={500}>
                          {user.points.balance.toLocaleString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          user.tier?.current_label
                            ? `${user.tier.current_label} 级别`
                            : `Lv.${user.points.growth_level} ${user.points.level_name}`
                        }
                        size="small"
                        variant="outlined"
                        color={user.tier?.tier_key && user.tier.tier_key !== 'free' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString('zh-CN')}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="调整等级 (LevelUp 2.0)">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => { setTierUser(user); setTierDialogOpen(true); }}
                        >
                          <EmojiEventsIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="编辑用户">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditUser(user)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="每页显示"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / 共 ${count}`}
        />
      </Paper>

      {/* 用户编辑对话框 */}
      <UserEditDialog
        open={editDialogOpen}
        user={editingUser}
        onClose={handleCloseEdit}
        onSuccess={handleEditSuccess}
      />

      {/* 等级调整对话框 (v3 LevelUp 2.0) */}
      <TierAdjustDialog
        open={tierDialogOpen}
        user={tierUser}
        onClose={() => { setTierDialogOpen(false); setTierUser(null); }}
        onSuccess={() => loadUsers()}
      />
    </Box>
  );
};

export default MongoUserManagement;
